import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { DefaultIntlFormatValues } from '@ratehub/base-ui';

import ProductFeatureList from './ProductFeatureList';


function ProductListSavingsDetails({ savingsAccount, ...otherProps }) {
    const items = createItems(savingsAccount);

    return (
        <MyProductFeatureList
            items={items}
            {...otherProps}
        />
    );
}

ProductListSavingsDetails.propTypes = {
    savingsAccount: PropTypes.shape({
        monthlyFee: PropTypes.number,
        waiveMonthlyFeeBalance: PropTypes.number,

        transactionFee: PropTypes.number,
        eTransferFee: PropTypes.number,
    }).isRequired,
};


const MyProductFeatureList = styled(ProductFeatureList)`
    max-width: 30em;
`;


function createItems(savingsAccount) {
    const {
        bonusInterest,
    } = savingsAccount;

    const savingsItems = [
        {
            key: (
                <FormattedMessage
                    id="web-components.savings.transaction-fees"
                    defaultMessage="Transaction fees"
                />
            ),
            value: <DollarsPerUse dollarValue={savingsAccount.transactionFee} />,
        },
        {
            key: (
                <FormattedMessage
                    id="web-components.savings.e-transfer-fees"
                    defaultMessage="Interac e-transfer"
                />
            ),
            value: <DollarsPerUse dollarValue={savingsAccount.eTransferFee} />,
        },
    ];

    if (bonusInterest) {
        // currently expecting 'YYYY-MM-DD' and if so, we must add a time to it, else we’ll lose a day in
        // translating from GMT to EST
        const expiryDateString = bonusInterest?.expiry.date
            ? /^\d{4}-\d\d-\d\d$/.test(bonusInterest.expiry.date)
                ? bonusInterest.expiry.date + ' 12:00'
                : bonusInterest.expiry.date // attempt to future-proof if API date response changes
            : undefined;

        savingsItems.unshift({
            key: (
                <FormattedMessage
                    id="web-components.edb.savings.promo-rate"
                    defaultMessage="Promo rate"
                />
            ),
            keyClassName: 'draw-attention weight-medium',
            value: (
                <b>
                    <FormattedMessage
                        key="big-number-regular-rate"
                        id="web-components.edb.savings.promo-rate.value"
                        defaultMessage={`{bonusInterestRate, number, percent2} {relative, select,
                            true { for the first {days, number} days}
                            other { until <longDate>{absoluteDate, date, long}</longDate>}
                        }`}
                        values={{
                            bonusInterestRate: bonusInterest.rate / 100,
                            relative: bonusInterest?.expiry.mode === 'relative',
                            days: bonusInterest?.expiry.days,
                            absoluteDate: expiryDateString && dayjs(expiryDateString).toDate(),
                            ...DefaultIntlFormatValues,
                        }}
                    />
                </b>
            ),
            valueClassName: 'draw-attention',
        });
    }

    return savingsItems;
}

function DollarsPerUse({ dollarValue }) {
    return (
        <Choose>
            <When condition={dollarValue}>
                <>
                    <b>
                        <FormattedNumber
                            value={dollarValue}
                            format="CAD2"
                        />
                    </b>
                    <FormattedMessage
                        id="web-components.edb.savings.per-use"
                        defaultMessage=" per use"
                    />
                </>
            </When>
            <Otherwise>
                <FormattedMessage
                    id="web-components.edb.savings.no-charge"
                    defaultMessage="No charge"
                />
            </Otherwise>
        </Choose>
    );
}

DollarsPerUse.propTypes = {
    dollarValue: PropTypes.number,
};


export default ProductListSavingsDetails;
